import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import { useChatModeState } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeState';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	isStoreAssistant,
	isStoreHuman,
	type StoreAssistant,
	type StoreHuman,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { findLast } from '../chat-store/findLast';
import { useChatStoreActions } from '../chat-store/useChatStoreActions';
import { useConversation } from '../conversation/useConversation';

const messages = defineMessages({
	forgeAuthFailureMessage: {
		id: 'ai-mate.pubsub-listener.forge-auth-faiure-message',
		defaultMessage: 'Confirmation failed, try again in a moment.',
		description: 'The message we send when forge authentication fails',
	},
	forgeAuthMessageCompleted: {
		id: 'ai-mate.pubsub-listener.forge-auth-completed-message',
		defaultMessage:
			'Authentication was successfully completed for the request in message {agentMessageId}. Please continue with the action from that message.',
		description:
			'Informational message sent to the agent when 3p authentication completes, referencing the specific agent message that initiated the auth flow',
	},
	forgeAuthCompletedMessageWithUnknownSource: {
		id: 'ai-mate.pubsub-listener.forge-auth-completed-message-with-unknown-source',
		defaultMessage:
			'Authenticated. You\u2019ve logged in, but we couldn\u2019t identify where the request came from. Continue with your action.',
		description:
			'Informational message sent to the agent when 3p authentication completes but the originating agent message is not known',
	},
	forgeAuthLinkLabel: {
		id: 'ai-mate.pubsub-listener.forge-auth-link-label',
		defaultMessage: 'auth link',
		description: 'Label text for the hyperlinked auth URL shown in the auth notification message',
	},
});

/**
 * Builds an ADF document containing a text message with an optional hyperlink.
 * When an authUrl is provided, the link label is appended as a separate inline text node
 * with a link mark so the raw URL is not directly visible.
 */
const buildAuthNotificationAdf = (text: string, linkLabel?: string, authUrl?: string): DocNode => {
	const contentNodes: Array<{
		type: 'text';
		text: string;
		marks?: Array<{ type: 'link'; attrs: { href: string } }>;
	}> = [{ type: 'text' as const, text }];

	if (authUrl && linkLabel) {
		contentNodes.push({
			type: 'text' as const,
			text: ` ${linkLabel}`,
			marks: [
				{
					type: 'link' as const,
					attrs: {
						href: authUrl,
					},
				},
			],
		});
	}

	return {
		version: 1,
		type: 'doc',
		content: [
			{
				type: 'paragraph',
				content: contentNodes,
			},
		],
	} as DocNode;
};

export const useForgeAuthController = (): {
	addForgeFailureMessage: (_errorMessage?: string) => void;
	handle3pActionAuth: (agentMessageId?: string, authUrl?: string) => void;
} => {
	const { conversation, selectedAgent } = useConversation();
	const conversationId = conversation?.id ?? EMPTY_CONVERSATION_ID;
	const [chatMessages] = useChatMessages({
		conversationId,
	});
	const { addAgentMessage, resolveConversationAction, sendMessage } = useChatStoreActions();
	const { formatMessage } = useIntl();
	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();
	const currentChatMode = useChatModeState(conversationId);

	const handle3pActionAuth = useCallback(
		(agentMessageId?: string, authUrl?: string): void => {
			if (fg('rovo_chat_fix_oauth_from_message_agent_ack')) {
				const authNotificationText = agentMessageId
					? formatMessage(messages.forgeAuthMessageCompleted, { agentMessageId })
					: formatMessage(messages.forgeAuthCompletedMessageWithUnknownSource);
				const linkLabel = formatMessage(messages.forgeAuthLinkLabel);

				const prompt = buildAuthNotificationAdf(authNotificationText, linkLabel, authUrl);

				sendMessage({
					conversationId,
					prompt,
					// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
					// value when BE user settings are fully rolled out; BE overrides it via the capability header.
					storeUserMessages,
					agentId: selectedAgent?.id,
					deepResearchEnabled: currentChatMode.deepResearchEnabled,
					thinkDeeperEnabled: currentChatMode.thinkDeeperEnabled,
					fastModeEnabled: currentChatMode.fastModeEnabled,
					taskModeEnabled: currentChatMode.taskModeEnabled,
					webSearchEnabled: currentChatMode.webSearchEnabled,
					alphaExtendedThinkingMode: currentChatMode.alphaExtendedThinkingMode,
					appFilters: currentChatMode.appFilters,
				});

				return;
			}

			const lastAgentMessage = findLast(chatMessages, isStoreAssistant) as
				| StoreAssistant
				| undefined;
			const lastAgentMessageActions = lastAgentMessage?.content.message.message.actions;

			if (!lastAgentMessage) {
				return;
			}

			// If the last message doesn't have actions, it means the auth url came before an action was planned
			// So we resend the prompt
			if (!lastAgentMessageActions?.length) {
				const lastHumanMessage = findLast(chatMessages, isStoreHuman) as StoreHuman | undefined;

				if (!lastHumanMessage) {
					return;
				}

				sendMessage({
					conversationId,
					prompt: encodeAdf(lastHumanMessage.content.content),
					storeUserMessages: false,
					agentId: selectedAgent?.id,
				});

				return;
			}

			// If the last message has actions, it means the auth url came after an action was planned
			// So we resolve the action, because we are now authenticated
			const action = lastAgentMessageActions[lastAgentMessageActions.length - 1];
			resolveConversationAction({
				conversationId,
				messageId: lastAgentMessage.content.message.message.id,
				payload: {
					context: {
						...action,
						resolution: {
							type: 'confirm',
						},
						messageId: lastAgentMessage.content.message.message.id,
						recipient_agent_named_id: selectedAgent?.named_id,
					},
				},
			});
		},
		[
			chatMessages,
			conversationId,
			currentChatMode,
			formatMessage,
			resolveConversationAction,
			selectedAgent?.id,
			selectedAgent?.named_id,
			sendMessage,
			storeUserMessages,
		],
	);

	const addForgeFailureMessage = useCallback(
		(_errorMessage?: string): void => {
			if (!conversation || !selectedAgent) {
				return;
			}
			addAgentMessage({
				conversationId: conversation.id,
				agentNamedId: selectedAgent?.named_id,
				content: formatMessage(messages.forgeAuthFailureMessage),
			});
		},
		[conversation, selectedAgent, addAgentMessage, formatMessage],
	);

	return { addForgeFailureMessage, handle3pActionAuth };
};
